import React, { useState, useEffect } from 'react';

// Import service icons
import squareIcon from '../../assets/square.png';
import posIcon from '../../assets/pos.png';
import digitalMarketIcon from '../../assets/digitalmarket.png';
import blockchainIcon from '../../assets/blockchain.png';

const serviceIcons = [
  { id: 1, icon: squareIcon, name: '3D Websites', delay: 0 },
  { id: 2, icon: posIcon, name: 'E-Commerce', delay: 0.2 },
  { id: 5, icon: digitalMarketIcon, name: 'Digital Marketing', delay: 0.4 },
  { id: 15, icon: blockchainIcon, name: 'Blockchain', delay: 0.6 },
];

const MobileHeroView = ({ 
  bannerData, 
  images, 
  currentImageIndex, 
  handleContactClick, 
  handleLearnMoreClick, 
  handleIndicatorClick,
  renderTitle,
  handleServiceClick 
}) => {
  const [selectedService, setSelectedService] = useState(null);
  const [serviceDetails, setServiceDetails] = useState({});
  const [loadingService, setLoadingService] = useState(false);
  const [autoRotateIndex, setAutoRotateIndex] = useState(0);
  const [isAutoRotating, setIsAutoRotating] = useState(true);
  const [showArrow, setShowArrow] = useState(false);
  const [userInteracted, setUserInteracted] = useState(false);
  const [autoRestartTimer, setAutoRestartTimer] = useState(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const currentImage = images[currentImageIndex] || '';

  // Smooth auto rotation effect
  useEffect(() => {
    if (!isAutoRotating || userInteracted) return;

    const rotationInterval = setInterval(() => {
      setIsTransitioning(true);
      
      setTimeout(() => {
        setAutoRotateIndex((prevIndex) => {
          const nextIndex = (prevIndex + 1) % serviceIcons.length;
          
          // Auto-fetch details for the next service
          const nextService = serviceIcons[nextIndex];
          if (!serviceDetails[nextService.id]) {
            fetchServiceDetails(nextService);
          }
          
          return nextIndex;
        });
        
        setIsTransitioning(false);
      }, 300); // Short delay for smooth transition
    }, 4000); // Change every 4 seconds

    return () => clearInterval(rotationInterval);
  }, [isAutoRotating, serviceDetails, userInteracted]);

  // Auto-select service based on rotation index when not manually selected
  useEffect(() => {
    if (!selectedService && serviceIcons.length > 0 && !userInteracted && !isTransitioning) {
      const currentService = serviceIcons[autoRotateIndex];
      if (!serviceDetails[currentService.id]) {
        fetchServiceDetails(currentService);
      }
    }
  }, [autoRotateIndex, selectedService, serviceDetails, userInteracted, isTransitioning]);

  // Hide arrow during auto rotation, show only when user manually selects
  useEffect(() => {
    if (selectedService && userInteracted) {
      setShowArrow(true);
    } else {
      setShowArrow(false);
    }
  }, [selectedService, userInteracted]);

  // Auto-restart rotation after 15 seconds of user interaction
  useEffect(() => {
    if (userInteracted && !isAutoRotating) {
      // Clear any existing timer
      if (autoRestartTimer) {
        clearTimeout(autoRestartTimer);
      }

      // Set new timer to restart auto rotation after 15 seconds
      const timer = setTimeout(() => {
        setIsAutoRotating(true);
        setUserInteracted(false);
        setSelectedService(null);
        setShowArrow(false);
      }, 15000); // 15 seconds

      setAutoRestartTimer(timer);

      return () => {
        if (autoRestartTimer) {
          clearTimeout(autoRestartTimer);
        }
      };
    }
  }, [userInteracted, isAutoRotating]);

  const fetchServiceDetails = async (service) => {
    if (!service) return;

    setLoadingService(true);
    try {
      const response = await fetch(`https://api.dremerz.net/api/DZ_8923_Idlbay_Services_ServicesList/${service.id}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data) {
        setServiceDetails(prev => ({
          ...prev,
          [service.id]: {
            title: data.title,
            description: data.description
          }
        }));
      }
    } catch (error) {
      console.error('Error fetching service details:', error);
      setServiceDetails(prev => ({
        ...prev,
        [service.id]: {
          title: service.name,
          description: 'Click to learn more about this service'
        }
      }));
    } finally {
      setLoadingService(false);
    }
  };

  const handleIconClick = (service) => {
    // Set user interaction flags
    setUserInteracted(true);
    setIsAutoRotating(false);
    setIsTransitioning(true);
    
    setTimeout(() => {
      if (selectedService && selectedService.id === service.id) {
        // If clicking the same service, deselect it
        setSelectedService(null);
        setShowArrow(false);
      } else {
        // Select new service
        setSelectedService(service);
        if (!serviceDetails[service.id]) {
          fetchServiceDetails(service);
        }
      }
      setIsTransitioning(false);
    }, 200);
  };

  const handleNavigateToService = () => {
    if (selectedService) {
      handleServiceClick(selectedService.id);
    }
  };

  // Reset auto rotation when component unmounts or user navigates away
  useEffect(() => {
    return () => {
      if (autoRestartTimer) {
        clearTimeout(autoRestartTimer);
      }
    };
  }, [autoRestartTimer]);

  // Determine which service to display details for
  const displayService = selectedService || serviceIcons[autoRotateIndex];
  const displayServiceDetails = serviceDetails[displayService?.id];

  return (
    <div 
      className={`idlbayhero-mobile-view ${selectedService ? 'idlbayhero-expanded' : ''}`}
      style={{
        backgroundImage: `linear-gradient(rgba(var(--color-primary-rgb, 74, 144, 226), 0.4), rgba(var(--color-bodyBlack-rgb, 0, 0, 0), 0.7)), url(${currentImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        position: 'relative',
        transition: 'height 0.4s ease-in-out'
      }}
    >
      {/* Mobile Background Overlay */}
      <div className="idlbayhero-mobile-overlay"></div>

      {/* Mobile Content - Centered */}
      <div className="idlbayhero-mobile-content">
        <div className="idlbayhero-mobile-text-content">
          {/* Title */}
          <h2 className="idlbayhero-mobile-title">
            {renderTitle()}
          </h2>
          
          {/* Description */}
          <h2 className="idlbayhero-mobile-description">
            {bannerData?.description}
          </h2>

          {/* Service Icons - Row wise for mobile */}
          <div className="idlbayhero-mobile-service-icons">
            {serviceIcons.map((service, index) => (
              <div 
                key={service.id}
                className={`idlbayhero-mobile-service-icon ${
                  displayService?.id === service.id ? 'idlbayhero-service-selected' : ''
                } ${isAutoRotating && !selectedService && autoRotateIndex === index ? 'idlbayhero-auto-rotating' : ''} ${
                  isTransitioning ? 'idlbayhero-auto-rotate-transition' : ''
                }`}
                onClick={() => handleIconClick(service)}
                role="button"
                tabIndex={0}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleIconClick(service);
                  }
                }}
              >
                <div className="idlbayhero-mobile-service-icon-image">
                  <img src={service.icon} alt={service.name} />
                </div>
              </div>
            ))}
          </div>

        {/* Service Details Section */}
        {displayService && (
          <div className="idlbayhero-service-details">
            <div className="idlbayhero-service-details-header">
              <h3 className="idlbayhero-service-details-title">
                {displayServiceDetails?.title || displayService.name}
              </h3>
              
              {/* Navigate Icon - Only show when user manually selects a service */}
              {showArrow && (
                <div 
                  className="idlbayhero-service-navigate-icon"
                  onClick={handleNavigateToService}
                  role="button"
                  tabIndex={0}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      handleNavigateToService();
                    }
                  }}
                >
                  <svg 
                    width="24" 
                    height="24" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path 
                      d="M13.5 4.5L21 12M21 12L13.5 19.5M21 12H3" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              )}
            </div>
            
            <div className="idlbayhero-service-details-content">
              {loadingService ? (
                <div className="idlbayhero-service-loading">
                  <div className="idlbayhero-spinner-small"></div>
                  <span>Loading service details...</span>
                </div>
              ) : (
                <p className="idlbayhero-service-details-description">
                  {displayServiceDetails?.description || 'Loading description...'}
                </p>
              )}
            </div>
          </div>
        )}
          
          {/* Buttons - Centered */}
          <div className="idlbayhero-mobile-buttons">
            <button 
              className="idlbayhero-btn idlbayhero-btn-primary idlbayhero-mobile-btn"
              onClick={handleContactClick}
            >
              {bannerData?.button1}
            </button>
            <button 
              className="idlbayhero-btn idlbayhero-btn-outline-light idlbayhero-mobile-btn"
              onClick={handleLearnMoreClick}
            >
              {bannerData?.button2}
            </button>
          </div>

          {/* Image Indicators for Mobile */}
          {images.length > 1 && (
            <div className="idlbayhero-mobile-indicators">
              {images.map((_, index) => (
                <button
                  key={index}
                  className={`idlbayhero-mobile-indicator ${index === currentImageIndex ? 'idlbayhero-active' : ''}`}
                  onClick={() => handleIndicatorClick(index)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MobileHeroView;